import React, {
    createContext,
    useContext,
    useState,
    useCallback,
    useRef,
    type ReactNode
} from 'react';
import { ToastContainer } from '../components/ui/Toast/ToastContainer';

export type ToastType = 'success' | 'error' | 'info' | 'warning';

export interface ToastMessage {
    id: string;
    type: ToastType;
    message: string;
    duration?: number;
}

interface ToastContextProps {
    addToast: (options: Omit<ToastMessage, 'id'>) => void;
    removeToast: (id: string) => void;
}

const ToastContext = createContext<ToastContextProps | undefined>(undefined);

export const ToastProvider = ({ children }: { children: ReactNode }) => {
    const [toasts, setToasts] = useState<ToastMessage[]>([]);
    // Используем ref для генерации уникальных id без зависимости от состояния
    const counterRef = useRef(0);

    const addToast = useCallback((options: Omit<ToastMessage, 'id'>) => {
        counterRef.current += 1;
        const id = `toast-${Date.now()}-${counterRef.current}`;
        setToasts(prev => [...prev, { id, ...options }]);
    }, []);

    const removeToast = useCallback((id: string) => {
        setToasts(prev => prev.filter(t => t.id !== id));
    }, []);

    return (
        <ToastContext.Provider value={{ addToast, removeToast }}>
            {children}
            <ToastPortal toasts={toasts} onRemove={removeToast} />
        </ToastContext.Provider>
    );
};

// Внутренний компонент-портал для рендера тостов
const ToastPortal = ({
    toasts,
    onRemove
}: {
    toasts: ToastMessage[];
    onRemove: (id: string) => void;
}) => {
    return <ToastContainer toasts={toasts} onRemove={onRemove} />;
};

export const useToast = (): ToastContextProps => {
    const context = useContext(ToastContext);
    if (!context) {
        throw new Error('useToast должен использоваться внутри <ToastProvider>');
    }
    return context;
};